import useScrollPosition from '@react-hook/window-scroll';
import { useWindowSize } from '@react-hook/window-size/throttled';
import clsx from 'clsx';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../context';
import { MarketingTeacher } from '../../../domain/teacher';
import { NAVBAR_HEIGHT } from '../../../domain/websiteDomain';
import useUserWithRedirects from '../../../hooks/useUser';
import SessionGrid from '../../sessions/sessionGrid/sessionGrid';
import HeroBanner from '../heroBanner/heroBanner';
import Metadata from '../metadata';
import About from './about';
import TeacherCTA from './cta';

const SESSIONS_ID = 'sessions';
const ABOUT_ID = 'about';

interface TeacherProfileProps {
  teacher: MarketingTeacher;
}

const TeacherProfile: React.FC<TeacherProfileProps> = ({ teacher }) => {
  const user = useUserWithRedirects();
  const { state } = useContext(AppContext);

  // distances from top of page to elements
  const [topDistances, setTopDistances] = useState<{ [key: string]: number }>(
    {}
  );
  // window resize hook
  const [width, height] = useWindowSize();

  const updateTopDistances = () => {
    if (typeof window !== 'undefined') {
      const updatedTopDistances = [SESSIONS_ID, ABOUT_ID].reduce((acc, cur) => {
        const element = document.getElementById(cur);

        const top =
          window.pageYOffset +
          (element?.getBoundingClientRect().top || 0) -
          NAVBAR_HEIGHT -
          47;

        acc[cur] = top;

        return acc;
      }, {} as { [key: string]: number });

      setTopDistances(updatedTopDistances);
    }
  };

  // effect that recalculates the top distances to elements on window resize and carousel rendering
  useEffect(() => {
    updateTopDistances();
  }, [width, height, state.carousels]);

  // window scroll position hook
  const scrollY = useScrollPosition();

  // the active section in view
  const [activeSection, setActiveSection] = useState<string | undefined>(
    undefined
  );

  // set active section depending on scroll position
  useEffect(() => {
    const sections = [
      {
        id: SESSIONS_ID,
        start: topDistances[SESSIONS_ID],
        end: topDistances[ABOUT_ID],
      },
      {
        id: ABOUT_ID,
        start: topDistances[ABOUT_ID],
        end: 99999999,
      },
    ];

    const section = sections.find(
      sec => scrollY > sec.start && scrollY < sec.end
    );

    if (activeSection !== section?.id) {
      setActiveSection(section?.id);
    }
  }, [scrollY]);

  const scrollToElement = (id: string) => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: topDistances[id] + 2, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Metadata
        title={`${teacher.name} | BML Online Hub`}
        description={teacher.bio}
        ogImage={teacher.bannerImage?.url}
      />
      <HeroBanner teacher={teacher} />

      <div className={`z-40 top-56px sticky`}>
        <ul className="flex flex-row items-center justify-center space-x-8 text-white bg-background-dark h-48px">
          {[SESSIONS_ID, ABOUT_ID].map(id => (
            <li
              key={`subnvav-${id}`}
              onClick={() => scrollToElement(id)}
              className={clsx(
                'cursor-pointer transition-colors duration-300',
                activeSection === id && 'text-accent-hover'
              )}
            >
              {id === SESSIONS_ID ? 'Classes' : 'About'}
            </li>
          ))}
        </ul>
      </div>

      {!user.isLoggedIn && <TeacherCTA teacher={teacher} />}

      <div id={SESSIONS_ID}>
        <SessionGrid
          title="All classes"
          teacher={teacher}
          onContentUpdate={updateTopDistances}
        />
      </div>

      <About id={ABOUT_ID} teacher={teacher} />

      {!user.isLoggedIn && <TeacherCTA teacher={teacher} />}
    </>
  );
};

export default TeacherProfile;
